import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#060C18] text-white">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 py-24">
        <h1 data-testid="privacy-title" className="text-4xl sm:text-5xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Privacy Policy
        </h1>
        <p className="text-white/40 mb-12">Last updated: April 2026</p>

        <div className="space-y-10 text-white/70 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Who we are</h2>
            <p>SendPI is a communication intelligence tool that helps users understand how their messages will land before they send them. We are operated as a pilot program currently available to selected users in the United States.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">What we collect</h2>
            <ul className="list-disc ml-5 space-y-2">
              <li><strong className="text-white">Account Information:</strong> name, email address when you create an account.</li>
              <li><strong className="text-white">Usage Data:</strong> which features you use, how often, and the results (tone preferences, acceptance rates).</li>
              <li><strong className="text-white">Anonymous Behavioral Metadata:</strong> communication patterns such as times of day you write most and relationship contexts.</li>
              <li><strong className="text-white">Technical Data:</strong> IP address, browser type, device type, and session cookies necessary for authentication.</li>
              <li><strong className="text-white">Consent Records:</strong> IP address, user agent, and electronic signature when you sign the pilot agreement.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">What we do NOT collect</h2>
            <p>SendPI <strong className="text-white">never collects or stores the actual content of your messages</strong>. When you type a message and SendPI analyzes it, that analysis happens through our AI service which does not retain your message text. The text of your messages is processed transiently and never stored in our database. This is a core design principle of SendPI, not just a policy.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Legal basis for processing (GDPR)</h2>
            <ul className="list-disc ml-5 space-y-2">
              <li><strong className="text-white">Consent:</strong> You explicitly consent to data processing through our Pilot Participation Agreement.</li>
              <li><strong className="text-white">Contractual necessity:</strong> Processing required to provide the SendPI service you signed up for.</li>
              <li><strong className="text-white">Legitimate interest:</strong> Product improvement, security, and abuse prevention.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">How we use your data</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>Provide and personalize the SendPI service</li>
              <li>Generate your communication progress insights</li>
              <li>Improve the product based on aggregate usage patterns</li>
              <li>Send product updates and feedback requests as part of the pilot</li>
              <li>Generate your weekly PI Insights report</li>
              <li>Ensure security and prevent abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Who we share data with</h2>
            <p>We do not sell, rent, or trade your personal data. We share data only with:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li>Cloud infrastructure providers (hosting, database) — minimum data required</li>
              <li>AI service providers (for PI analysis) — message text processed transiently, not stored</li>
              <li>Law enforcement — only when legally required</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Your rights under GDPR (EU/EEA residents)</h2>
            <p>If you are located in the EU/EEA, you have the following rights under the General Data Protection Regulation:</p>
            <ul className="list-disc ml-5 space-y-2">
              <li><strong className="text-white">Right of Access (Art. 15):</strong> Request a copy of all personal data we hold about you.</li>
              <li><strong className="text-white">Right to Rectification (Art. 16):</strong> Correct inaccurate personal data.</li>
              <li><strong className="text-white">Right to Erasure (Art. 17):</strong> Delete your account and all associated data at any time via Settings.</li>
              <li><strong className="text-white">Right to Restrict Processing (Art. 18):</strong> Request we limit how we use your data.</li>
              <li><strong className="text-white">Right to Data Portability (Art. 20):</strong> Download all your data in machine-readable JSON format from Settings.</li>
              <li><strong className="text-white">Right to Object (Art. 21):</strong> Object to processing based on legitimate interest.</li>
              <li><strong className="text-white">Right to Withdraw Consent:</strong> Withdraw consent at any time by deleting your account.</li>
            </ul>
            <p className="mt-3">To exercise any of these rights, use the self-service options in your dashboard Settings, or email <a href="mailto:support@sendpi.ai" className="text-cyan-400 hover:underline">support@sendpi.ai</a>. We will respond within 30 days.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Your rights under CCPA (California residents)</h2>
            <p>If you are a California resident, you have the following rights under the California Consumer Privacy Act:</p>
            <ul className="list-disc ml-5 space-y-2">
              <li><strong className="text-white">Right to Know:</strong> Request details about the personal information we collect, use, and share.</li>
              <li><strong className="text-white">Right to Delete:</strong> Request deletion of your personal information.</li>
              <li><strong className="text-white">Right to Opt-Out of Sale:</strong> We do <strong>not</strong> sell your personal information. See our <Link to="/do-not-sell" className="text-cyan-400 hover:underline">Do Not Sell page</Link>.</li>
              <li><strong className="text-white">Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your CCPA rights.</li>
            </ul>
            <p className="mt-3">To exercise these rights, email <a href="mailto:support@sendpi.ai" className="text-cyan-400 hover:underline">support@sendpi.ai</a> or use account Settings. We verify your identity before processing requests.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Cookies</h2>
            <p>SendPI uses only <strong className="text-white">strictly necessary cookies</strong> for authentication:</p>
            <ul className="list-disc ml-5 space-y-1">
              <li><code className="text-cyan-400 bg-white/5 px-1 rounded">access_token</code> — HTTP-only, Secure, session authentication (7 days)</li>
              <li><code className="text-cyan-400 bg-white/5 px-1 rounded">refresh_token</code> — HTTP-only, Secure, session refresh (30 days)</li>
            </ul>
            <p className="mt-3">We do not use advertising, tracking, or analytics cookies. These cookies are essential for the service to function and cannot be disabled.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Data retention</h2>
            <p>We keep your data for as long as your account is active. When you delete your account, all your data is permanently and immediately deleted from all our systems. We do not retain deleted data.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Data transfers</h2>
            <p>Your data is processed and stored in the United States. If you are outside the US, by using SendPI you consent to this transfer. We implement appropriate safeguards including encryption in transit (TLS) and at rest.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Security</h2>
            <ul className="list-disc ml-5 space-y-1">
              <li>Passwords hashed with bcrypt (12 rounds)</li>
              <li>HTTP-only, Secure cookies with SameSite protection</li>
              <li>Security headers (HSTS, X-Frame-Options, CSP)</li>
              <li>Rate limiting and brute force protection</li>
              <li>Audit logging of all security-relevant actions</li>
              <li>Three-tier access control (User, Admin, Support)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Children's privacy</h2>
            <p>SendPI is not intended for children under 16. We do not knowingly collect data from children under 16.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Changes to this policy</h2>
            <p>We may update this privacy policy. We will notify you by email of significant changes. The "Last updated" date at the top indicates the most recent revision.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">Contact & Data Protection</h2>
            <p>For all privacy questions, data requests, or complaints: <a href="mailto:support@sendpi.ai" className="text-cyan-400 hover:underline">support@sendpi.ai</a></p>
            <p className="mt-2">If you believe your privacy rights have been violated, you have the right to lodge a complaint with your local data protection authority.</p>
          </section>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-wrap gap-6">
          <Link to="/terms" className="text-white/50 hover:text-white transition-colors">Terms of Service</Link>
          <Link to="/do-not-sell" className="text-white/50 hover:text-white transition-colors">Do Not Sell My Information</Link>
          <Link to="/" className="text-white/50 hover:text-white transition-colors">Back to Home</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
